import { Button, Col, Form, Input, Modal, Row } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { RouteChildrenProps, useHistory } from "react-router";
import { Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";
import {
  VALIDATION_MESSAGE_CONFIG,
  FormChangeDebounceTime,
  UserActionEnum,
  ReportStatesEnum,
} from "../../../../../constants/constants";
import { ROUTE_PATHS } from "../../../../../constants/url-config";
import { MessageType } from "../../../../../models/common/response-message";
import { FormSectionModel } from "../../../../../models/form-section.model";
import { ResourcesModel } from "../../../../../models/resources.model";
import { ScenarioModel } from "../../../../../models/scenario.model";
import {
  setActions,
  setStatus,
  setTitle,
} from "../../../../../redux/actions/header.action";
import { ClientService } from "../../../../../services/client.service";
import { ERMService } from "../../../../../services/erm.service";
import i18n from "../../../../../utils/i18n";
import UTIL from "../../../../../utils/utils";
import AppAnchor from "../../../../atoms/anchor";
import { showMessage } from "../../../../atoms/notification/notification";
import DivisionSection from "./division-section";
import EstimatedDirectImpactSection from "./estimated-direct-impact-section";
import EstimatedIndirectImpactSection from "./estimated-indirect-impact-section";
import EstimateLikelihoodSection from "./estimated-likelihood-section";
import InformationSection from "./information-section";
import PlaningSection from "./planing-section";
import RecoverySection from "./recovery-information-section";
import RiskTypeSection from "./risk-type-section";
import "./scenarios-form.scss";

const ScenariosFormSections: FormSectionModel[] = [
  {
    title: i18n.t("Scenario.planingTitle"),
    id: 1,
    is_completed: false,
    sectionComponent: PlaningSection,
  },
  {
    title: i18n.t("Scenario.divisionSectionTitle"),
    id: 2,
    is_completed: false,
    sectionComponent: DivisionSection,
  },
  {
    title: i18n.t("Scenario.informationTitle"),
    id: 3,
    is_completed: false,
    sectionComponent: InformationSection,
  },
  {
    title: i18n.t("Scenario.estimatedLikelihoodTitle"),
    id: 4,
    is_completed: false,
    sectionComponent: EstimateLikelihoodSection,
  },
  {
    title: i18n.t("Scenario.estimatedDirectImpactTitle"),
    id: 5,
    is_completed: false,
    sectionComponent: EstimatedDirectImpactSection,
  },
  {
    title: i18n.t("Scenario.estimatedIndirectImpactTitle"),
    id: 6,
    is_completed: false,
    sectionComponent: EstimatedIndirectImpactSection,
  },
  {
    title: i18n.t("Scenario.expectedRecoveryTitle"),
    id: 7,
    is_completed: false,
    sectionComponent: RecoverySection,
  },
  {
    title: i18n.t("Scenario.riskTypeSectionTitle"),
    id: 8,
    is_completed: false,
    sectionComponent: RiskTypeSection,
  },
];

const formChanged = new Subject<ScenarioModel>();
interface Props extends RouteChildrenProps<{ id: string }> {}

const ScenarioForm = (props: Props) => {
  const history = useHistory();
  const { t } = useTranslation();
  const [resources, setResources] = useState<ResourcesModel>({});
  const [form] = Form.useForm();
  const [data, setData] = useState<ScenarioModel>({});
  const [formValidateTrigger, setFormValidateTrigger] = useState(["onFinish"]);
  const [requiredFields, setRequiredFields] = useState<string[]>([]);
  const valuesChanged = useRef<ScenarioModel>({});
  const id = useRef(0);
  const dispatch = useDispatch();

  const next = () => {
    if (!id.current) {
      showMessage({
        message: t("Global.noFillFormError"),
        type: MessageType.error,
      });
      return;
    }
    setFormValidateTrigger(["onFinish", "onChange"]);
    form.submit();
  };

  const autoSave = () => {
    if (valuesChanged.current.scenario_event_experiences) {
      valuesChanged.current.scenario_event_experiences = form.getFieldValue(
        "scenario_event_experiences"
      );
    }

    const value = UTIL.prepareScenatioDesignFormForSubmit(
      valuesChanged.current
    );
    valuesChanged.current = {};

    if (id.current) {
      ERMService.saveDraftLostScenarioAsync(id.current, value, false).then(
        (res) => {
          updateData(res.data);
        }
      );
    } else {
      ERMService.newLostScenarioAsync(value).then((res) => {
        if (res.data) {
          id.current = res.data.id;
          updateData(res.data);
        }
      });
    }
  };

  const saveDraft = () => {
    if (!id.current) {
      showMessage({
        message: t("Global.noFillFormError"),
        type: MessageType.error,
      });
      return;
    }
    const value = UTIL.prepareScenatioDesignFormForSubmit(
      valuesChanged.current
    );
    ERMService.saveDraftLostScenarioAsync(id.current, value, true).then(
      (res) => {
        showMessage({
          message: t("Global.lbSavedSuccessfully"),
          type: MessageType.success,
        });
        history.push(ROUTE_PATHS.Scenarios);
      }
    );
  };

  const saveLater = () => {
    Modal.confirm({
      content: t("Global.lbConfirmSave"),
      onOk: saveDraft,
      icon: "",
      width: 304,
      okText: t("Global.lbSave"),
      cancelText: t("Global.lbCancel"),
    });
  };

  const renderActions = () => (
    <>
      <Button
        onClick={saveLater}
        htmlType="button"
        type="default"
        shape="round"
        block
      >
        {t("Scenario.btnSave")}
      </Button>
      <Button
        onClick={next}
        htmlType="button"
        type="primary"
        shape="round"
        block
      >
        {t("Scenario.btnNext")}
      </Button>
    </>
  );

  const formChange = (changedValues: ScenarioModel) => {
    changedValues = resetChildrenSelect(changedValues);
    valuesChanged.current = { ...valuesChanged.current, ...changedValues };
    formChanged.next(valuesChanged.current);
  };

  const resetChildrenSelect = (changedValues: ScenarioModel) => {
    const o = { ...changedValues };
    if (o.hasOwnProperty("category_id")) {
      form.resetFields(["category_level_2_id", "category_level_3_id"]);
      changedValues = {
        ...changedValues,
        category_level_2_id: undefined,
        category_level_3_id: undefined,
      };
    }

    if (o.hasOwnProperty("category_level_2_id")) {
      form.resetFields(["category_level_3_id"]);
      changedValues = { ...changedValues, category_level_3_id: undefined };
    }

    if (o.hasOwnProperty("designer_division_id")) {
      form.resetFields(["designer_business_unit_id"]);
      changedValues = {
        ...changedValues,
        designer_business_unit_id: undefined,
      };
    }

    if (o.hasOwnProperty("scenario_event_experiences")) {
      if (
        changedValues.scenario_event_experiences &&
        changedValues.scenario_event_experiences.length
      ) {
        const length = changedValues.scenario_event_experiences?.length;
        const scenario_event_experiences = {
          ...changedValues.scenario_event_experiences[length - 1],
        };
        if (scenario_event_experiences.hasOwnProperty("division_id")) {
          form.resetFields([
            ["", length - 1, "business_unit_id"],
            ["scenario_event_experiences", length - 1, "svp_id"],
          ]);
        }
      }
    }

    return changedValues;
  };

  const formSubmit = (value: ScenarioModel) => {
    ERMService.updateLostScenarioAsync(id.current, value).then((res) => {
      showMessage({
        message: t("Global.lbSavedSuccessfully"),
        type: MessageType.success,
      });
      history.push({
        pathname: `${ROUTE_PATHS.ViewScenario}/${value.id}`,
        search: `?type=${UserActionEnum.Review}`,
      });
    });
  };

  const updateData = (val: ScenarioModel) => {
    setData(val);
    UTIL.setSectionIsCompleted(val._section_statuses, ScenariosFormSections);
    setRequiredFields(UTIL.getAllRequiredFields(val._sections));
  };

  useEffect(() => {
    const formChangedSub = formChanged
      .pipe(debounceTime(FormChangeDebounceTime))
      .subscribe((value) => {
        if (value) {
          autoSave();
        }
      });

    id.current = Number(props.match?.params.id);

    if (id.current) {
      ERMService.getLostScenarioAsyns(id.current).then((res) => {
        if (res.data) {
          const newData = UTIL.convertEditScenatioDesignForm(res.data);
          form.setFieldsValue(newData);
          updateData(res.data);

          if (newData.submit_state === ReportStatesEnum.Draft) {
            dispatch(setTitle(newData.short_name_for_scenario || " "));
            const dom = UTIL.getLossStatesDOM(newData.submit_state);
            const status = (
              <span
                className="text-status"
                style={{ color: dom.color, backgroundColor: dom.bgColor }}
              >
                {dom.text}
              </span>
            );
            dispatch(setStatus(status));
          }
        }
      });
    }

    dispatch(setActions(renderActions()));

    ClientService.getResourcesAsync().then((res) => {
      setResources(res.data);
    });
    return () => {
      dispatch(setActions(""));
      dispatch(setStatus(""));
      dispatch(setTitle(""));
      formChangedSub.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="scenarios-form-wrapper">
      <Row gutter={20}>
        <Col xs={0} sm={5}>
          <AppAnchor sections={ScenariosFormSections} />
        </Col>
        <Col xs={0} sm={14}>
          <Form
            className="main-form"
            form={form}
            layout="vertical"
            initialValues={{
              scenario_event_experiences: [{}],
            }}
            validateMessages={VALIDATION_MESSAGE_CONFIG}
            onValuesChange={(
              changedValues: ScenarioModel,
              values: ScenarioModel
            ) => formChange(changedValues)}
            validateTrigger={formValidateTrigger}
            onFinish={formSubmit}
            scrollToFirstError
          >
            <Form.Item name="id" hidden={true}>
              <Input />
            </Form.Item>

            {ScenariosFormSections.map((item, index) => (
              <div
                id={`section${item.id}`}
                key={index}
                className="form-section"
              >
                <p className="section-title">{item.title}</p>
                <item.sectionComponent
                  data={data}
                  resources={resources}
                  requiredFields={requiredFields}
                />
              </div>
            ))}

            <Row gutter={12}>
              <Col span={24} className="form-action">
                {renderActions()}
              </Col>
            </Row>
          </Form>
        </Col>
        <Col xs={0} sm={5}></Col>
      </Row>
    </div>
  );
};

export default ScenarioForm;
